import { useCallback } from 'react'

import { FvButton, ValidatedForm } from '@fv/client-components'

import { DividedContent } from '@/components/DividedContent'
import { PillHeader } from '@/components/PillHeader'
import { maxItemCount } from '@/constants'
import arrayOfLength from '@/utils/arrayOfLength'

import { LoadItemForm } from './LoadItemForm'
import { useItemActions, useItemsState } from './loadItemsContext'

type Props = {
  onSubmit?: () => void
}
export const PagedItemsFormWrapper = ({ onSubmit }: Props) => {
  const { items, activeIndex, itemForm } = useItemsState()
  const {
    addItem,
    validateForm,
    clearCurrentItem,
    updateCurrentItem,
    setFormRef,
    duplicateCurrentItem: duplicateItem,
  } = useItemActions()
  const canAddItem = items.length < maxItemCount

  const handleAddClick = () => {
    if (validateForm()) {
      addItem()
      itemForm?.reset()
    }
  }
  const formRef = useCallback(
    (ref: HTMLFormElement | null) => {
      if (ref !== itemForm) {
        setFormRef(ref)
      }
    },
    [itemForm, setFormRef],
  )
  return (
    <div>
      <ValidatedForm
        autoReportNextError={false}
        ref={formRef}
        onValidSubmit={onSubmit}
      >
        <div className="form-row">
          <ItemNav />
          <LoadItemForm
            value={items[activeIndex]}
            onChange={updateCurrentItem}
          />
        </div>
        <button className="hidden" type="submit" />
      </ValidatedForm>
      <DividedContent className="pt-4">
        <div>
          <FvButton icon="plus" disabled={!canAddItem} onClick={handleAddClick}>
            Add handling unit
          </FvButton>
        </div>
        <div>
          <FvButton disabled={!canAddItem} icon="clone" onClick={duplicateItem}>
            Duplicate handling unit
          </FvButton>
        </div>
        <div>
          <FvButton icon="times" onClick={clearCurrentItem}>
            Clear
          </FvButton>
        </div>
      </DividedContent>
    </div>
  )
}

const ItemNav = () => {
  const { items, activeIndex, itemForm, disabled } = useItemsState()
  const {
    activateItem,
    addItem,
    removeCurrentItem: removeItem,
    validateForm,
  } = useItemActions()
  const itemCount = items.length
  const canAddItem = items.length < maxItemCount

  const handleAddClick = () => {
    if (validateForm()) {
      addItem()
      itemForm?.reset()
    }
  }

  return (
    <PillHeader className="beer-container !border-2 py-[.225rem]">
      <div className="flex flex-nowrap items-center">
        <span className="whitespace-nowrap">Handling units</span>
        <div className="px-2 flex flex-1 space-x-2 overflow-x-auto ">
          {arrayOfLength(itemCount).map(ix => (
            <FvButton
              theme={ix !== activeIndex ? 'underlined' : 'pagination'}
              active={ix === activeIndex}
              key={ix}
              onClick={() => activateItem(ix)}
            >
              {ix + 1}
              {items[ix].handlingUnitType === 'multi' && (
                <span className="text-xs">M</span>
              )}
            </FvButton>
          ))}
        </div>
        <div className="flex flex-nowrap">
          <FvButton
            className="ml-1 bg-white rounded-full h-6 w-6 border-fv-blue border-2 flex items-center justify-center [&>svg]:hover:text-[#333]"
            disabled={disabled || !canAddItem}
            icon="plus"
            onClick={handleAddClick}
            title="Add item"
          />

          {itemCount > 1 && (
            <FvButton
              className="ml-2"
              disabled={disabled}
              iconClass="text-fv-orange-bright"
              icon="trash"
              onClick={removeItem}
              title="Remove item"
              transform="shrink-2"
            />
          )}
        </div>
      </div>
    </PillHeader>
  )
}
